import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { DropzoneArea } from 'mui-file-dropzone';
import { Alert, FormControl, Grid, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { ConfigContext } from 'components/ConfigGuard';
import { FormProps, getValidationProps } from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';

import commonStyles from 'styles/common.module.scss';
import styles from './FileUploadFormItem.module.scss';

interface FileUploadFormItemProps extends Omit<FormProps, 'label'> {
  fieldName?: string;
  label?: string;
}

const MAX_FILE_SIZE = 2000000;
const MAX_FILES_COUNT = 1;

const FileUploadFormItem: React.FunctionComponent<FileUploadFormItemProps> = ({
  fieldName = 'files',
  label = 'Upload File',
  required = false,
  disabled,
}) => {
  const { isBayViewTheme } = useContext(ConfigContext);
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const skipFirst = React.useRef(true);
  const { error } = getValidationProps(`${fieldName}`, errors);

  const validateFilesField = useCallback(
    (value: File[]) => {
      return required ? !!value?.length : true;
    },
    [required]
  );

  return (
    <Grid {...defaultGridContainerProps}>
      {error && (
        <Grid {...defaultGridItemProps}>
          <Alert severity="error" className={commonStyles.alert} style={{ marginTop: '1rem' }}>
            Please upload a file.
          </Alert>
        </Grid>
      )}
      <Grid {...defaultGridItemProps}>
        <FormControl required={required} error={error} className={styles.formControl}>
          {isBayViewTheme ? (
            <>
              <h3>Birth Certificate {required ? <span className={commonStyles.requiredAsterisk}>*</span> : ''}</h3>
              <Typography sx={{ mb: 3 }} variant="body1">
                Required for children under 18
              </Typography>
            </>
          ) : (
            <>
              <h3>
                {label} {required ? <span className={commonStyles.requiredAsterisk}>*</span> : ''}
              </h3>
            </>
          )}
          <Controller
            name={`${fieldName}`}
            control={control}
            defaultValue={[]}
            rules={{
              validate: validateFilesField,
            }}
            render={({ field }) => (
              <>
                <DropzoneArea
                  maxFileSize={MAX_FILE_SIZE}
                  filesLimit={MAX_FILES_COUNT}
                  fileObjects={field.value ?? []}
                  onChange={(newFileObjects) => {
                    if (skipFirst.current) {
                      skipFirst.current = false;
                      return;
                    }
                    field.onChange(newFileObjects);
                  }}
                  onDelete={field.onChange}
                  showAlerts={['error']}
                  clearOnUnmount={true}
                  showPreviews
                  showPreviewsInDropzone={false}
                  useChipsForPreview
                  previewText=""
                  classes={{
                    root: classNames(styles.dropzone, { [styles.disabled]: disabled }),
                    textContainer: styles.dropzoneTextContainer,
                    text: styles.dropzoneText,
                    icon: styles.dropzoneIcon,
                  }}
                  previewGridClasses={{
                    container: styles.previewContainer,
                    item: styles.previewImageContainer,
                  }}
                  dropzoneProps={{ disabled, noDrag: false }}
                  previewChipProps={{ className: styles.chip, disabled }}
                  alertSnackbarProps={{
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  }}
                />
              </>
            )}
          />
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default FileUploadFormItem;
